import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { communitiesPaths } from "@circle-react/helpers/routerPathHelpers";
import { Icon } from "@circleco/compass/components/Icon";
import { Link, useMatchRoute } from "@tanstack/react-router";

import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";

import { usePlatformMenuV3 } from "../SidebarV2/PlatformMenuV3/usePlatformMenuV3";
import { SidebarItem } from "./SidebarItem";
import { useSidebarNavLinkClass } from "./useSidebarNavLinkClass";

export interface PlatformMenuSectionProps {
  isDisabled?: boolean;
  isFeedLinkActive?: boolean;
}

export const PlatformMenuSection = ({
  isDisabled = false,
  isFeedLinkActive = false,
}: PlatformMenuSectionProps) => {
  const { getActiveInactiveClasses } = useSidebarNavLinkClass();
  const { shouldShowOnboarding, hideOnboardingModal } = usePlatformMenuV3();
  const { currentCommunity } = usePunditUserContext();
  const { home_page_enabled: shouldShowFeed } = currentCommunity ?? {};
  const matchRoute = useMatchRoute();
  const feedMatch = matchRoute({ to: communitiesPaths.feed() });
  const onboardingMatch = matchRoute({ to: communitiesPaths.onboarding() });

  const isOnboardingActive = Boolean(onboardingMatch);
  const isFeedActive = Boolean(feedMatch) || isFeedLinkActive;
  if (!shouldShowOnboarding && !shouldShowFeed) {
    return null;
  }

  const handleCloseOnboarding = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    void hideOnboardingModal.show();
  };

  return (
    <nav aria-label={t("secondary_navigation_bar")}>
      <ul className="flex list-none flex-col gap-y-1">
        {shouldShowOnboarding && (
          <li>
            <SidebarItem
              isActive={isOnboardingActive}
              isDisabled={isDisabled}
              hoverScope="scoped"
              rootElement={<Link to={communitiesPaths.onboarding()} />}
            >
              <SidebarItem.Content
                label={t("getting_started")}
                startIcon="to-do-list"
                endContent={
                  <button
                    aria-label={t("close")}
                    className={classNames(
                      "shrink-0 rounded-md p-0.5",
                      getActiveInactiveClasses(isOnboardingActive),
                    )}
                    type="button"
                    onClick={handleCloseOnboarding}
                    disabled={isDisabled}
                  >
                    <Icon name="cross-small" size="sm" />
                  </button>
                }
              />
            </SidebarItem>
          </li>
        )}
        {shouldShowFeed && (
          <li>
            <SidebarItem
              isActive={isFeedActive}
              isDisabled={isDisabled}
              rootElement={<Link to={communitiesPaths.feed()} />}
            >
              <SidebarItem.Content label={t("feed")} startIcon="bullet-list" />
            </SidebarItem>
          </li>
        )}
      </ul>
    </nav>
  );
};
